/**
 * M95 — deterministic escalation curves for long Stage 2 levels.
 * All functions are pure: same inputs → same outputs (no RNG).
 */

import type { EnemyRank } from '../../combat/types/primitives'
import type { LevelArcPhaseId } from './levelPlan'
import { arcPhaseForWave, type LevelPlan } from './levelPlan'

export interface EscalateContext {
  ordinal: number
  waveIndex: number
  waveCount: number
  bossLevel: boolean
  phaseId: LevelArcPhaseId
}

export function escalateContext(plan: LevelPlan, waveIndex: number): EscalateContext {
  const waveCount = plan.waveCountTarget
  const phase = arcPhaseForWave(plan, waveIndex)
  return {
    ordinal: plan.ordinal,
    waveIndex,
    waveCount,
    bossLevel: plan.bossLevel,
    phaseId: phase?.id ?? 'develop'
  }
}

/** Base enemy level at wave 1 for a Chapter 2 ordinal (M97 long-level curve). */
export function baseEnemyLevelForOrdinal(ordinal: number): number {
  const n = Math.max(1, Math.min(30, Math.floor(ordinal)))
  if (n <= 8) return 2 + Math.floor((n - 1) / 3)
  if (n <= 16) return 4 + Math.floor((n - 9) / 4)
  if (n <= 24) return 5 + Math.floor((n - 17) / 4)
  return 6 + Math.floor((n - 25) / 3)
}

const PHASE_LEVEL_BIAS: Record<LevelArcPhaseId, number> = {
  intro: 0,
  develop: 0,
  pressure: 0,
  checkpoint: 1,
  climax: 1,
  boss: 1
}

/**
 * Enemy level for a wave — rises with level ordinal, arc phase, and late-wave index.
 */
export function enemyLevelForWave(plan: LevelPlan, waveIndex: number): number {
  const ctx = escalateContext(plan, waveIndex)
  const progress = (waveIndex - 1) / Math.max(1, ctx.waveCount - 1)
  const waveRamp = Math.floor(progress * 2)
  const phaseBias = PHASE_LEVEL_BIAS[ctx.phaseId]
  const bossBump = ctx.phaseId === 'boss' ? 1 : 0
  return (
    baseEnemyLevelForOrdinal(ctx.ordinal) + waveRamp + phaseBias + bossBump
  )
}

/** Rank curve — ELITE reserved for late climax/boss; pressure elites are rarer on long levels. */
export function enemyRankForWave(plan: LevelPlan, waveIndex: number): EnemyRank {
  const ctx = escalateContext(plan, waveIndex)
  if (ctx.phaseId === 'boss') return 'ELITE'
  if (ctx.phaseId === 'climax' && waveIndex >= ctx.waveCount - 1) return 'ELITE'
  if (
    ctx.phaseId === 'checkpoint' &&
    waveIndex % 7 === 0 &&
    waveIndex > ctx.waveCount * 0.5
  ) {
    return 'ELITE'
  }
  return 'NORMAL'
}

/**
 * Suggested total spawn count for a single-enemy wave (authors split into groups).
 * Scales with arc phase and match length.
 */
export function spawnCountForWave(plan: LevelPlan, waveIndex: number): number {
  const ctx = escalateContext(plan, waveIndex)
  const progress = (waveIndex - 1) / Math.max(1, ctx.waveCount - 1)
  // M97: flatter counts so long matches stay decision-rich without early wipe.
  const base = 3 + Math.floor(ctx.ordinal / 6)
  const lengthScale = Math.floor(ctx.waveCount / 14)
  const phaseBonus =
    ctx.phaseId === 'pressure' || ctx.phaseId === 'checkpoint'
      ? 1
      : ctx.phaseId === 'climax' || ctx.phaseId === 'boss'
        ? 2
        : 0
  const ramp = Math.floor(progress * 3)
  return base + lengthScale + phaseBonus + ramp
}

/** Spawn interval (seconds) — slightly more prep breathing room on long levels. */
export function spawnIntervalForWave(plan: LevelPlan, waveIndex: number): number {
  const ctx = escalateContext(plan, waveIndex)
  if (ctx.phaseId === 'intro') return 0.95
  if (ctx.phaseId === 'develop') return 0.85
  if (ctx.phaseId === 'pressure') return 0.75
  if (ctx.phaseId === 'checkpoint') return 0.7
  if (ctx.phaseId === 'climax') return 0.65
  return 0.6
}

/** Intra-wave group delay stagger (seconds). */
export function groupDelayStagger(plan: LevelPlan, waveIndex: number): number {
  const ctx = escalateContext(plan, waveIndex)
  if (ctx.phaseId === 'boss') return 0.55
  if (ctx.phaseId === 'climax') return 0.45
  return 0.35
}

/**
 * Per-wave completion coin reward — flatter on long matches so 30–40 wave levels
 * do not explode total Coins vs legacy short tables.
 */
export function completionRewardForWave(
  plan: LevelPlan,
  waveIndex: number
): number {
  const ctx = escalateContext(plan, waveIndex)
  const base = 28 + Math.floor(ctx.ordinal * 1.6)
  const progress = waveIndex / Math.max(1, ctx.waveCount)
  const arcBonus =
    ctx.phaseId === 'climax' || ctx.phaseId === 'boss'
      ? 16 + Math.floor(ctx.ordinal * 0.35)
      : Math.floor(progress * 10)
  const bossFinish =
    ctx.phaseId === 'boss' && waveIndex === ctx.waveCount ? 28 : 0
  // Dampen per-wave payout as match length grows (keeps total near ~14-wave family).
  const lengthDamp = Math.max(0.5, Math.min(1, 14 / Math.max(10, ctx.waveCount)))
  return Math.round((base + arcBonus + bossFinish) * lengthDamp)
}
